import React, { useEffect, useState } from 'react';

// mui
import { Autocomplete, Paper, Table, TableBody, TableCell, TableContainer, TableRow, TextField } from '@mui/material';

// components
import { optionsFilterType, optionsFilterGroup, cloneObject, getFilterStatusString } from '../..';

// client-shared
import { storage } from '@goldblack/client-storage';
import { ContainerScroll, ListboxComponent } from '@goldblack/client-comps';

function updateField(filterId, field, value) {
    const filters = storage.transience.getValue('account.filter');
    const found = filters.data.find((f) => f._id === filterId);
    const updated = cloneObject(found);
    updated[field] = value;
    storage.transience.updateState('account.filter.data', (f) => f._id === filterId, updated);
}

function FilterName({ filter }) {
    const onBlur = (e) => updateField(filter._id, 'name', e.currentTarget.innerText);
    return (
        <Paper
            sx={{ backgroundColor: 'background.default' }}
            suppressContentEditableWarning
            contentEditable
            onBlur={onBlur}
            elevation={0}>
            {filter.name}
        </Paper>
    );
}

function FilterType({ filter }) {
    const onChange = (e, value) => updateField(filter._id, 'type', value ?? '');
    return (
        <Autocomplete
            sx={{ m: 0, p: 0 }}
            options={optionsFilterType}
            value={optionsFilterType.find((x) => x === filter.type) ?? null}
            autoHighlight
            slotProps={{ listbox: { component: ListboxComponent } }}
            renderInput={(params) => <TextField {...params} size='small' />}
            onChange={onChange}
        />
    );
}

function FilterGroup({ filter }) {
    const onChange = (e, values) => {
        const flags = values.reduce((acc, v) => acc | v.code, 0);
        updateField(filter._id, 'groupFlags', flags);
    };
    const selected = optionsFilterGroup.filter((o) => (filter.groupFlags & o.code) !== 0);
    return (
        <Autocomplete
            sx={{ m: 0, p: 0 }}
            multiple
            options={optionsFilterGroup}
            value={selected}
            getOptionLabel={(o) => o.label}
            isOptionEqualToValue={(o, v) => o.code === v.code}
            disableCloseOnSelect
            slotProps={{ listbox: { component: ListboxComponent } }}
            renderInput={(params) => <TextField {...params} size='small' />}
            onChange={onChange}
        />
    );
}

function AccountFiltersFilterInfoPanel({ filter }) {
    const [createdAt, setCreatedAt] = useState('');
    const [updatedAt, setUpdatedAt] = useState('');

    useEffect(() => {
        if (filter) {
            setCreatedAt(filter.createdAt ? new Date(filter.createdAt).toLocaleString('ko-KR') : '');
            setUpdatedAt(filter.updatedAt ? new Date(filter.updatedAt).toLocaleString('ko-KR') : '');
        }
    }, [filter]);

    return (
        <ContainerScroll>
            <TableContainer>
                <Table sx={{ '& td, & th': { border: 1, borderColor: 'divider' } }}>
                    <TableBody>
                        <TableRow>
                            <TableCell sx={{ width: '30%' }}>name</TableCell>
                            <TableCell sx={{ width: '70%' }} align='right'>
                                <FilterName filter={filter} />
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>type</TableCell>
                            <TableCell align='right'>
                                <FilterType filter={filter} />
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>status</TableCell>
                            <TableCell align='right'>{getFilterStatusString(filter.status)}</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>groupFlags</TableCell>
                            <TableCell align='right'>
                                <FilterGroup filter={filter} />
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>createdAt</TableCell>
                            <TableCell align='right'>{createdAt}</TableCell>
                        </TableRow>
                        <TableRow>
                            <TableCell>updatedAt</TableCell>
                            <TableCell align='right'>{updatedAt}</TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>
        </ContainerScroll>
    );
}

export { AccountFiltersFilterInfoPanel };
